import { Form } from "@inertiajs/react"
import { CheckCircle2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { transitionsMissionCollabPath } from "@/routes"
import type { BrandMissionCollabRow } from "@/types/missions"

import { ReturnToFields } from "./collab-transition-return"

/**
 * Brand-side `Mark complete` confirm dialog (UX Spec §2.5).
 *
 * Wraps Inertia `<Form>` (Crew rule) — no shadcn Form, no RHF, no zod.
 * Hidden `event=mark_complete` input drives the controller dispatch.
 *
 * Default focus on the destructive primary action per UX Spec §6 #7
 * (operator's desired outcome — Cancel-by-default would slow the brand
 * workflow).
 */
interface MarkCompleteConfirmDialogProps {
  row: BrandMissionCollabRow
  missionId: number
}

export function MarkCompleteConfirmDialog({
  row,
  missionId,
}: MarkCompleteConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ariaTitle = row.creator.handle
    ? `@${row.creator.handle}`
    : row.creator.name

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant="default"
            size="sm"
            aria-label={t("collabs.mark_complete.aria", { title: ariaTitle })}
            data-testid="brand-mission-collab-mark-complete-trigger"
          >
            <CheckCircle2 aria-hidden="true" className="mr-1 size-4" />
            {t("collabs.mark_complete.cta")}
          </Button>
        }
      />
      <DialogContent
        data-testid="brand-mission-collab-mark-complete-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{t("collabs.mark_complete.title")}</DialogTitle>
          <DialogDescription>
            {t("collabs.mark_complete.body")}
          </DialogDescription>
        </DialogHeader>
        <Form
          action={transitionsMissionCollabPath(missionId, row.id)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="event" value="mark_complete" />
              {/* OKS-1038 — return_to/collabs_shown on the crew member page only. */}
              <ReturnToFields />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  data-testid="brand-mission-collab-mark-complete-cancel"
                >
                  {t("collabs.mark_complete.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={processing}
                  autoFocus
                  data-testid="brand-mission-collab-mark-complete-confirm"
                >
                  {t("collabs.mark_complete.confirm")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
